import { fetchGoogleOneTapConfig } from "@app/server/client";

// Under FedCM only the skipped/dismissed moments are reported.
type PromptMomentNotification = {
  isSkippedMoment: () => boolean;
  isDismissedMoment: () => boolean;
};

// Minimal slice of the Google Identity Services API we use.
// https://developers.google.com/identity/gsi/web/reference/js-reference
type GoogleIdentity = {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: { credential?: string }) => void;
        nonce?: string;
        context?: "signin" | "signup" | "use";
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
        itp_support?: boolean;
        use_fedcm_for_prompt?: boolean;
      }) => void;
      prompt: (listener?: (moment: PromptMomentNotification) => void) => void;
      cancel: () => void;
    };
  };
};

declare global {
  interface Window {
    google?: GoogleIdentity;
  }
}

const GIS_SCRIPT_URL = "https://accounts.google.com/gsi/client";

let gisScript: Promise<GoogleIdentity> | null = null;

const loadGoogleIdentity = () => {
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  gisScript ??= new Promise<GoogleIdentity>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GIS_SCRIPT_URL;
    script.async = true;
    script.onload = () =>
      window.google?.accounts?.id
        ? resolve(window.google)
        : reject(new Error("Google Identity Services did not load."));
    script.onerror = () => {
      gisScript = null;
      script.remove();
      reject(new Error("Google Identity Services did not load."));
    };
    document.head.appendChild(script);
  });
  return gisScript;
};

export type GoogleOneTapCredential = {
  credential: string;
  state: string;
};

type OneTapStatus = "loading" | "ready" | "prompting" | "unavailable";

export type GoogleOneTapController = {
  /**
   * Called from the Google button. Shows the One Tap sheet (or keeps the one
   * already on screen) and only runs `fallback` — the redirect flow — if
   * Google skips the prompt: cooldown after a dismissal, no signed-in
   * accounts, or FedCM unavailable. Returns false when One Tap can't be used
   * at all, in which case the caller should redirect straight away.
   */
  promptOrFallback: (fallback: () => void) => boolean;
  cancel: () => void;
};

/**
 * Shows Google's One Tap prompt. Where the browser supports FedCM (Chrome on
 * Android and desktop) this is the browser's own account sheet; elsewhere GIS
 * falls back to its in-page prompt. Failures are silent because the redirect
 * flow is always there as the fallback.
 */
export const startGoogleOneTap = (input: {
  context: "signin" | "signup";
  onCredential: (credential: GoogleOneTapCredential) => void;
}): GoogleOneTapController => {
  let status: OneTapStatus = "loading";
  let google: GoogleIdentity | null = null;
  let pendingFallback: (() => void) | null = null;
  let cancelled = false;

  const onMoment = (moment: PromptMomentNotification) => {
    if (moment.isSkippedMoment()) {
      if (status === "prompting") status = "ready";
      const fallback = pendingFallback;
      pendingFallback = null;
      fallback?.();
    } else if (moment.isDismissedMoment() && status === "prompting") {
      // Credential returned or cancel() called: nothing to fall back to.
      status = "ready";
      pendingFallback = null;
    }
  };

  const prompt = () => {
    if (!google) return;
    status = "prompting";
    google.accounts.id.prompt(onMoment);
  };

  // Only use One Tap where the browser has FedCM (Chrome on Android/desktop).
  // Without it GIS falls back to its legacy in-page prompt, which ITP breaks
  // on Safari and every iOS browser (all WebKit) and which reports a "not
  // displayed" moment we don't handle — leaving the button waiting on a
  // prompt that never appears. Those browsers use the redirect flow instead.
  // Embedded WebViews are excluded too: Google blocks sign-in inside them.
  const hasFedCM = "IdentityCredential" in window;
  if (!hasFedCM || (globalThis as any).ReactNativeWebView) {
    status = "unavailable";
  } else {
    (async () => {
      try {
        const [config, gis] = await Promise.all([
          fetchGoogleOneTapConfig(),
          loadGoogleIdentity(),
        ]);
        if (cancelled) return;
        if (!config) {
          status = "unavailable";
          return;
        }

        google = gis;
        gis.accounts.id.initialize({
          client_id: config.clientId,
          nonce: config.nonce,
          context: input.context,
          auto_select: false,
          cancel_on_tap_outside: false,
          itp_support: true,
          use_fedcm_for_prompt: true,
          callback: ({ credential }) => {
            if (credential && !cancelled) {
              input.onCredential({ credential, state: config.state });
            }
          },
        });
        prompt();
      } catch (error) {
        status = "unavailable";
        console.warn("Google One Tap unavailable", error);
      }
    })();
  }

  return {
    promptOrFallback: (fallback) => {
      if (status === "ready") {
        pendingFallback = fallback;
        prompt();
        return true;
      }
      if (status === "prompting") {
        // The sheet is already up (or about to be); tapping the button must
        // not navigate away from under it.
        pendingFallback = fallback;
        return true;
      }
      return false;
    },
    cancel: () => {
      cancelled = true;
      status = "unavailable";
      pendingFallback = null;
      google?.accounts.id.cancel();
    },
  };
};
